import { useEffect, useRef, useCallback, useState } from 'react';
import { io, Socket } from 'socket.io-client';
import { PresenceUser } from '../types.ts';

/** Event received from other collaborators via WebSocket */
export interface DataChangeEvent {
  type: string;
  data?: Record<string, unknown>;
  projectId?: string;
  timestamp?: string;
}

interface CollaborationHook {
  connected: boolean;
  presence: PresenceUser[];
  broadcastChange: (type: string, data: Record<string, unknown>) => void;
  onDataChanged: (callback: (event: DataChangeEvent) => void) => () => void;
}

/**
 * Custom hook for real-time collaboration via WebSocket.
 *
 * Usage:
 * ```tsx
 * const { connected, presence, broadcastChange } = useCollaboration(projectId);
 *
 * // When saving a person:
 * broadcastChange('person_updated', { id: person.id, name: person.name });
 * ```
 */
export function useCollaboration(projectId: string | undefined): CollaborationHook {
  const [connected, setConnected] = useState(false);
  const [presence, setPresence] = useState<PresenceUser[]>([]);
  const socketRef = useRef<Socket | null>(null);
  const dataChangeCallbacksRef = useRef<Array<(event: DataChangeEvent) => void>>([]);

  useEffect(() => {
    if (!projectId) return;

    // Initialize WebSocket connection (auth via httpOnly cookie)
    const socket = io(window.location.origin, {
      withCredentials: true,
    });

    socketRef.current = socket;

    // Connection events
    socket.on('connect', () => {
      setConnected(true);
      socket.emit('join_project', projectId);
    });

    socket.on('disconnect', () => {
      setConnected(false);
    });

    socket.on('connect_error', (err) => {
      console.error('❌ WebSocket connection error:', err.message);
      setConnected(false);
    });

    // Presence updates
    socket.on('presence_update', (updatedPresence: PresenceUser[]) => {
      setPresence(updatedPresence);
    });

    // Data change events (server emits 'data_update' to other clients)
    socket.on('data_update', (event) => {
      dataChangeCallbacksRef.current.forEach((callback) => callback(event));
    });

    // Real-time notification push
    socket.on('notification', () => {
      window.dispatchEvent(new CustomEvent('ws-notification'));
    });

    // Cleanup on unmount.
    //
    // `disconnect()` staat bewust BUITEN de connected-check: socket.io-client
    // reconnect standaard oneindig, dus een socket die op dit moment nog aan het
    // (re)connecten is bleef anders eeuwig doorproberen — inclusief alle
    // listeners, die via hun closures de hele hook-state vasthielden. Bij elke
    // project-wissel tijdens verbindingsverlies kwam er zo één bij.
    return () => {
      if (socket.connected) socket.emit('leave_project', projectId);
      socket.disconnect();
    };
  }, [projectId]);

  /**
   * Broadcast a data change to all connected users in the project.
   *
   * @param type - Type of change (e.g., 'person_updated', 'project_settings_changed')
   * @param data - Data associated with the change
   */
  const broadcastChange = useCallback(
    (type: string, data: Record<string, unknown>) => {
      if (socketRef.current?.connected && projectId) {
        socketRef.current.emit('data_changed', {
          type,
          projectId,
          data,
          timestamp: new Date().toISOString(),
        });
      } else {
        console.warn('⚠️  Cannot broadcast change: WebSocket not connected');
      }
    },
    [projectId]
  );

  /**
   * Register a callback to be invoked when other users make changes.
   *
   * @param callback - Function to call when data changes
   */
  const onDataChanged = useCallback((callback: (event: DataChangeEvent) => void) => {
    dataChangeCallbacksRef.current.push(callback);

    // Return cleanup function
    return () => {
      dataChangeCallbacksRef.current = dataChangeCallbacksRef.current.filter(
        (cb) => cb !== callback
      );
    };
  }, []);

  return {
    connected,
    presence,
    broadcastChange,
    onDataChanged,
  };
}
